/**
 * Custom hook for fetching job posting data using TanStack Query
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiGet } from '@/lib/api/api-client'
import type { JobPosting } from '@/types'

/**
 * Fetches a job posting by ID from the recruitment API
 */
async function fetchJobPosting(jobPostingId: string): Promise<JobPosting> {
  return apiGet<JobPosting>(`/recruitment/job-postings/${jobPostingId}`)
}

/**
 * Hook for fetching job posting data with caching and retry logic
 *
 * @param jobPostingId - The ID of the job posting to fetch
 * @returns TanStack Query result with job posting data
 */
export function useJobPosting(
  jobPostingId: string
): UseQueryResult<JobPosting, Error> {
  return useQuery({
    queryKey: ['jobPosting', jobPostingId],
    queryFn: () => fetchJobPosting(jobPostingId),
    retry: (failureCount, error: Error) => {
      const statusCode = (error as Error & { statusCode?: number })?.statusCode
      if (statusCode && statusCode >= 400 && statusCode < 500) {
        return false
      }
      return failureCount < 3
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}
